import angular, { IHttpService, ILogService, IPromise, IQService } from 'angular';

const LOCALES: Locale[] = [
    {
        name: "Canada",
        localeID: "en_CA",
        languageCode: "en",
        countryCode: "CA",
        countryID: 2 // Apex country IDs.
    },
    {
        name: "US",
        localeID: "en_US",
        languageCode: "en",
        countryCode: "US",
        countryID: 1
    }
];

const PRIMARY_LOCALE = LOCALES[0];

export interface Locale {
    name: string;
    localeID: string;
    languageCode: string;
    countryCode: string;
    countryID: number;
}

export class LocaleService {
    constructor(private $q: IQService, private $http: IHttpService, private $log: ILogService) {
        'ngInject'
    }

    retrieveLocales(): IPromise<Locale[]> {
        return this.$q.resolve(LOCALES);
    }

    retrieveCurrentLocale(): IPromise<Locale> {
        // Retrieve the first locale defined that matches one of the following:
        // 1. The fixed locale that should be displayed on the specific page.
        // 2. The user's locale preference, as selected in the locale switcher.
        // 3. The user's default locale, based on site data.
        // 4. The locale that matches the country where the user's IP is located.
        // 5. PRIMARY_LOCALE constant.

        let localeID: string | IPromise<string> = localStorage.getItem("apexLocale");
        if (!localeID) {
            // Look up country by IP address.
            const ipApiUrl = "https://ip2c.org/self";
            localeID = this.$http.get<string>(ipApiUrl, { withCredentials: false, headers: { Accept: "text/html" } })
                .then(function (res) {
                    // This particular API only returns results in string format, eg: 1;CA;CAN;CANADA
                    return "en_" + res.data.split(";")[1];
                })
                .catch(ex => {
                    this.$log.warn(ex, "IP Lookup returned an error. Falling back to en_US");
                    return "en_US";
                });
        }

        return this.$q.resolve(localeID)
            .then(function (localeID) {
                return LOCALES.filter(function (locale) {
                    return locale.localeID === localeID;
                })[0] || PRIMARY_LOCALE;
            });
    }

    retrieveLocaleByLocaleID(localeID: string): IPromise<Locale> {
        return this.$q.resolve(LOCALES.find(locale => locale.localeID === localeID));
    }

    retrieveCurrentLocaleTranslations(): IPromise<any> {
        return this.retrieveCurrentLocale()
            .then(locale => {
                if (!locale) return {};
                return this.retrieveLocaleTranslations(locale.localeID);
            });
    }

    retrieveLocaleTranslations(localeID: string): any {
        return this.retrieveLocaleByLocaleID(localeID)
            .then(locale => {
                const languageBase = this.retrieveFileContents("resources/locale-" + locale.languageCode + ".json");
                const countryTranslations = this.retrieveFileContents("resources/locale-" + locale.localeID + ".json")
                return this.$q.all([languageBase, countryTranslations]);
            })
            .then(([languageBase, countryTranslations]) => {
                return angular.extend({}, languageBase, countryTranslations);
            })
            .catch(function () {
                return {};
            });
    }


    setCurrentLocale(localeID: string) {
        return this.$q.resolve(null)
            .then(function () {
                localStorage.setItem("apexLocale", localeID);
            });
    }

    setDefaultLocale(localeID: string) {
        return this.$q.resolve(null)
            .then(() => {
                // If the current locale is not set, set it to the default locale.
                const locale = localStorage.getItem("apexLocale");
                if (!locale) {
                    return this.setCurrentLocale(localeID);
                }
            });
    }

    private retrieveFileContents(url: string): any {
        return this.$http.get(url)
            .then(function (response) {
                return response.data;
            });
    }
}
